import { useCallback, useEffect, useRef } from 'react';
import { followIntoViewWithinContainer } from '@/shared/lib';
import { getScrollAreaViewport } from '../lib/getScrollAreaViewport';
import { highlightCloud } from '../lib/highlightCloud';
import type { ShowTranscript } from './types';

/**
 * Переходы в переписке: скрытую на своей панели сначала показать, а к облаку сообщения текстовой
 * коммуникации — перейти по запросу или при открытии карточки на найденном сообщении.
 *
 * @param messageIdParam значение условия открытия на найденном сообщении (`?messageId=`)
 */
export const useTranscriptJumps = (messageIdParam: string | null) => {
  // Показ переписки, скрытой на своей панели; не зарегистрирован — переписка видна всегда
  const showTranscriptRef = useRef<ShowTranscript | null>(null);

  // Переход к месту переписки: скрытую на своей панели сначала показать, иначе прокручивать некуда
  const jumpInTranscript = useCallback((jump: () => void) => {
    const showTranscript = showTranscriptRef.current;
    if (showTranscript) showTranscript(jump);
    else jump();
  }, []);

  // Облака элементов переписки по id — цели перехода к сообщению. Облака держит лента переписки,
  // а переход зовут из вкладки «Вложения», поэтому встречаются они в сессии диалога
  const messageElementsRef = useRef(new Map<number, HTMLElement>());

  // Снятие удержания облака предыдущего перехода: новый переход уводит ленту к другому облаку
  const stopMessageFollowRef = useRef<(() => void) | null>(null);

  useEffect(() => () => stopMessageFollowRef.current?.(), []);

  // К верхнему краю облака: над ним время отправки, а длинное письмо в середину области не уместилось бы.
  // Облако удерживается, пока лента догружает картинки: только что показанная, она растёт уже во время прокрутки
  const followMessage = useCallback((messageElement: HTMLElement | undefined) => {
    stopMessageFollowRef.current?.();
    stopMessageFollowRef.current = followIntoViewWithinContainer(messageElement, {
      align: 'start',
      behavior: 'smooth',
      container: getScrollAreaViewport(messageElement),
    });
  }, []);

  // Сообщение из условия открытия ?messageId=, к которому ещё не перешли: его облака нет, пока лента
  // не загружена и не показана, — переход выполняет регистрация облака
  const pendingMessageIdRef = useRef<number | null>(null);

  // Сообщение из условия открытия, к которому переходили без показа переписки: раскладка с перепиской
  // на своей панели (узкий экран) появляется уже после первой отрисовки — ширину экрана карточка узнаёт
  // позже. Показ переписки для него ещё не выполнялся
  const unrevealedMessageIdRef = useRef<number | null>(null);

  // Облако берётся в момент перехода: в скрытой панели переписки его ещё нет. Переход к другому
  // сообщению заменяет открытие на найденном — оно больше не перехватит ленту
  const scrollToMessage = useCallback(
    (messageId: number) => {
      pendingMessageIdRef.current = null;
      unrevealedMessageIdRef.current = null;
      jumpInTranscript(() => followMessage(messageElementsRef.current.get(messageId)));
    },
    [jumpInTranscript, followMessage],
  );

  const jumpToPendingMessage = useCallback(() => {
    const messageId = pendingMessageIdRef.current;
    const messageElement = messageId == null ? undefined : messageElementsRef.current.get(messageId);
    if (!messageElement) return;
    pendingMessageIdRef.current = null;
    followMessage(messageElement);
    highlightCloud(messageElement);
  }, [followMessage]);

  const registerMessageElement = useCallback(
    (messageId: number, element: HTMLElement) => {
      const messageElements = messageElementsRef.current;
      messageElements.set(messageId, element);
      if (messageId === pendingMessageIdRef.current) jumpToPendingMessage();
      return () => {
        if (messageElements.get(messageId) === element) messageElements.delete(messageId);
      };
    },
    [jumpToPendingMessage],
  );

  // Первая регистрация показа после открытия на сообщении показывает переписку и переходит к сообщению
  // заново: прежний переход мог пройти в ленте прежней раскладки, которой уже нет
  const registerShowTranscript = useCallback(
    (show: ShowTranscript) => {
      showTranscriptRef.current = show;
      const unrevealedMessageId = unrevealedMessageIdRef.current;
      if (unrevealedMessageId != null) {
        unrevealedMessageIdRef.current = null;
        pendingMessageIdRef.current = unrevealedMessageId;
        show(jumpToPendingMessage);
      }
      return () => {
        if (showTranscriptRef.current === show) showTranscriptRef.current = null;
      };
    },
    [jumpToPendingMessage],
  );

  // Открытие текстовой коммуникации на найденном сообщении: один раз на значение ?messageId= — прочие
  // зависимости постоянны. Скрытая на своей панели переписка сначала показывается — иначе облако не появится.
  // Повторный запуск после снятия эффекта (StrictMode в разработке) переходит заново: удержание облака
  // снимается вместе с карточкой, и без нового перехода облако уехало бы, пока лента догружается
  useEffect(() => {
    const messageId = Number(messageIdParam);
    if (!messageIdParam || Number.isNaN(messageId)) return;
    pendingMessageIdRef.current = messageId;
    unrevealedMessageIdRef.current = showTranscriptRef.current ? null : messageId;
    jumpInTranscript(jumpToPendingMessage);
    return () => {
      pendingMessageIdRef.current = null;
      unrevealedMessageIdRef.current = null;
    };
  }, [messageIdParam, jumpInTranscript, jumpToPendingMessage]);

  return { jumpInTranscript, scrollToMessage, registerMessageElement, registerShowTranscript };
};
